import { databaseProviderSchema, databaseConnectionSchema, resolvedDatabaseConnectionSchema } from "./database-connections.js";
import { z } from "zod";
import { postgresConnectionStatusSchema } from "./postgres-connections.js";
/*
 * Credentials a running turn's tools resolve through the tenant API. The executor holds no
 * secret-store, database or admin credential of its own: each request carries the run's lease,
 * and the tenant answers only while that attempt holds the run and only within the run's own
 * tenant, organisation and user.
 */
const id = z.string().trim().min(1).max(200);
const lease = {
    runId: id, tenantSlug: id, workerId: id,
    attemptNumber: z.number().int().positive(),
};
/**
 * A plugin secret as `@mlx/plugin-secrets` addresses it, without its store location: the tenant
 * derives the path from the scope, which must be the run's own.
 */
export const workerPluginSecretReferenceSchema = z.object({
    provider: z.literal("infisical"),
    tenantSlug: id,
    pluginSlug: id,
    scope: z.enum(["tenant", "organisation", "user"]).optional(),
    userId: id.optional(),
    organisationId: id.optional(),
    secretName: id,
}).strict();
export const workerCredentialRequestSchema = z.discriminatedUnion("operation", [
    z.object({ ...lease, operation: z.literal("database.list"), provider: databaseProviderSchema, grant: z.unknown() }).strict(),
    z.object({ ...lease, operation: z.literal("database.connection"), provider: databaseProviderSchema, connectionId: id, grant: z.unknown() }).strict(),
    z.object({ ...lease, operation: z.literal("plugin.resolve"), reference: workerPluginSecretReferenceSchema }).strict(),
    z.object({
        ...lease, operation: z.literal("plugin.upsert"), reference: workerPluginSecretReferenceSchema,
        value: z.string().min(1).max(200_000),
    }).strict(),
    /** An OAuth application or Composio credential registered for the run's tenant in admin-control. */
    z.object({
        ...lease, operation: z.literal("plugin.app"),
        providerType: z.enum(["native-oauth", "composio"]), providerSlug: id,
    }).strict(),
    /** Marks the run user's Google Workspace secrets unconfigured after Google rejected the grant. */
    z.object({
        ...lease, operation: z.literal("google.invalidate"), pluginSlug: id,
        secretNames: z.array(id).min(1).max(10), credentialReadStartedAt: z.number().int().nonnegative(),
    }).strict(),
    /** One Postgres plugin connection of the run's organisation, with its credentials. */
    z.object({
        ...lease, operation: z.literal("postgres.connection"), connectionId: id,
        expectedRevision: z.number().int().positive().optional(),
        acceptedStatuses: z.array(postgresConnectionStatusSchema).min(1).max(3),
    }).strict(),
    /** An organisation connector's configuration, without its credentials. */
    z.object({
        ...lease, operation: z.literal("connector.integration"),
        provider: z.enum(["github", "overture", "sharepoint", "xero"]), organisationId: id,
    }).strict(),
    /*
     * An organisation connector's configuration and the credential a live tool call needs, within
     * one of the run's organisations. A read names the source entity it reads; the tenant
     * authorises it against the run's warehouse grant before it returns any credential. Xero and
     * SharePoint access tokens are refreshed by the tenant, under its lock, near expiry.
     */
    z.object({
        ...lease, operation: z.literal("connector.access"),
        provider: z.enum(["github", "overture", "sharepoint", "xero"]), organisationId: id,
        expectedIntegrationId: id.optional(),
        read: z.object({ sourceEntity: z.string().trim().min(1).max(200).nullable(), grant: z.unknown() }).strict().optional(),
    }).strict(),
    /** The relations a native read of one source entity may return, authorised against the run's warehouse grant. */
    z.object({
        ...lease, operation: z.literal("native.read"),
        provider: id, organisationId: id, sourceEntity: z.string().trim().min(1).max(200).nullable(),
        credentialScope: z.enum(["organisation", "personal"]), connectorIntegrationId: id.optional(), grant: z.unknown(),
    }).strict(),
]);
export const workerPostgresConnectionSecretsSchema = z.object({
    connectionUrl: z.string().min(1),
    tlsCaPem: z.string().min(1).optional(),
    tlsClientCertPem: z.string().min(1).optional(),
    tlsClientKeyPem: z.string().min(1).optional(),
}).strict();
export const workerCredentialResponseSchema = z.discriminatedUnion("operation", [
    z.object({ operation: z.literal("database.list"), connections: z.array(databaseConnectionSchema) }).strict(),
    z.object({ operation: z.literal("database.connection"), resolved: resolvedDatabaseConnectionSchema }).strict(),
    z.object({ operation: z.literal("plugin.resolve"), value: z.string() }).strict(),
    z.object({ operation: z.literal("plugin.upsert") }).strict(),
    z.object({ operation: z.literal("plugin.app"), credential: z.record(z.string(), z.unknown()) }).strict(),
    z.object({ operation: z.literal("google.invalidate"), invalidated: z.number().int().nonnegative() }).strict(),
    z.object({
        operation: z.literal("postgres.connection"),
        /** The connection's metadata as `/api/worker/plugins/postgres-connections` serialises it. */
        connection: z.record(z.string(), z.unknown()),
        secrets: workerPostgresConnectionSecretsSchema,
    }).strict(),
    z.object({
        operation: z.literal("connector.integration"),
        integration: z.object({ id, organisationId: id, config: z.record(z.string(), z.unknown()) }).strict(),
    }).strict(),
    z.object({
        operation: z.literal("connector.access"),
        integration: z.object({ id, organisationId: id, config: z.record(z.string(), z.unknown()) }).strict(),
        /** The provider's credential for this call: an access token, a static token or client credentials. */
        secrets: z.record(z.string(), z.string()),
    }).strict(),
    z.object({
        operation: z.literal("native.read"),
        relations: z.array(z.object({ schemaName: z.string(), relationName: z.string() }).strict()),
    }).strict(),
]);
/** Stable refusal codes; the executor turns each into the tool error the agent sees. */
export const WORKER_CREDENTIAL_REFUSALS = {
    leaseNotHeld: "credential_lease_not_held",
    scopeMismatch: "credential_scope_mismatch",
    unavailable: "credential_unavailable",
    connectionNotFound: "postgres_connection_not_found",
    connectionNotHealthy: "postgres_connection_not_healthy",
    connectionRevisionStale: "postgres_connection_revision_stale",
    connectorNotFound: "connector_not_found",
    connectorChanged: "connector_changed",
    readDenied: "native_read_denied",
};
